import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './TimeDelay.css';
import {
  Grid, TextField, Select, MenuItem, ButtonGroup,
  Radio, RadioGroup, FormControlLabel, FormControl,
} from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import Button from '@mui/material/Button';
import { getTimeDelayStore } from './useTimeDelayStore';
import { timezones } from '../../utils/timezones';
import { send_request } from '../../utils/Request';
import Autocomplete from '@mui/material/Autocomplete';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import checkKey from '../../utils/CheckKey';
import ChipInput from '../ChipInput/ChipInput';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import { getTime, setMonth } from '../../utils/TimeUtil';

const pad2 = (n) => String(n).padStart(2, '0');

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TimeDelayTemplate = (props) => {
  const { component_id } = props;

  const store = useMemo(() => getTimeDelayStore(component_id), [component_id]);
  const storeData = store((s) => s.data);

  // Local UI state — numeric codes for radio/select, arrays for ChipInput, etc.
  const [componentType, setComponentType] = useState(0);
  const [period, setPeriod] = useState(3);
  const [timezone, setTimezone] = useState('UTC');
  const [day, setDay] = useState('');
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [errorMessage, setErrorMessage] = useState('');
  const [checkKeyError, setCheckKeyError] = useState(false);
  const [focus, setFocus] = useState('');
  const [delayBy, setDelayBy] = useState([]);
  const [atDatetimePlaceholder, setAtDatetimePlaceholder] = useState([]);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const focusRef = useRef(focus);
  useEffect(() => { focusRef.current = focus; }, [focus]);

  // Sync local UI state from store data
  const syncLocalState = useCallback((d) => {
    if (!d) return;
    setComponentType(store.getState().configComponentTypeValue(d.mode || d.componentType));
    setDelayBy(d.delayAmount ? [String(d.delayAmount)] : d.delayBy ? [String(d.delayBy)] : []);
    setAtDatetimePlaceholder(d.datetimePlaceholder ? [String(d.datetimePlaceholder)] : d.atDatetimePlaceholder ? [String(d.atDatetimePlaceholder)] : []);
    setPeriod(d.delayUnit ? store.getState().configIntervalValue(d.delayUnit) : d.delayInterval ? store.getState().configIntervalValue(d.delayInterval) : 3);
    setTimezone(d.timezone);
    setDay(d.dayOfWeek || d.day);
    if (d.date) {
      const cleaned = typeof d.date === 'string' && d.date.endsWith('Z') ? d.date.slice(0, -1) : d.date;
      const dt = new Date(cleaned);
      setDate(isNaN(dt.getTime()) ? new Date() : dt);
    }
  }, [store]);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      if (component_id === undefined) return;

      if (store.getState().loaded) {
        props.onComponentNameChanged(store.getState().name);
        props.onLastModifiedChanged(store.getState().lastModified);
        if (!cancelled) syncLocalState(store.getState().data);
      } else {
        const json = await send_request(
          `project-service/project/component/${component_id}`, '', {}
        ).catch((err) => { console.log(err); });

        if (json?.data) {
          const components = json.data;
          store.getState().setComponentData(components);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(components.description);
          props.onLastModifiedChanged(store.getState().lastModified);
          if (!cancelled) syncLocalState(store.getState().data);
        }
      }

      if (!cancelled) setLoading(false);
      props.setChildTemplateLoaded(true);
    };

    loadData();
    return () => { cancelled = true; };
  }, []);

  // Placeholder selection listener
  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== component_id) return;
        if (!placeholder?.selected || typeof placeholder.selected !== 'object') return;
        const { style, key, isNotPlaceholder } = placeholder.selected;
        if (!style) return;
        const text = isNotPlaceholder ? key : '${' + key + '}';
        if (focusRef.current === 'delayBy') {
          setDelayBy([text]);
          store.getState().setData(text, 'delayAmount');
        } else if (focusRef.current === 'atDatetimePlaceholder') {
          setAtDatetimePlaceholder([text]);
          store.getState().setData(text, 'datetimePlaceholder');
        }
      }
    );
    return unsub;
  }, []);

  const handleEditPlaceholder = (event) => {
    const val = replacePlaceholderValue(event.target.value);
    store.getState().setPlaceholder(val);
    const [hasError, msg] = checkKey(val, null, []);
    setCheckKeyError(hasError);
    setErrorMessage(msg);
  };

  const handleSetDateTime = (value) => {
    if (!value) return;
    const dt = value.toDate ? value.toDate() : new Date(value);
    if (isNaN(dt.getTime())) return;
    const dateStr = `${dt.getFullYear()}-${setMonth(dt.getMonth() + 1)}-${pad2(dt.getDate())}T${getTime(dt)}`;
    setDate(dateStr);
    store.getState().setData(dateStr, 'date');
  };

  if (loading) return <IndividualComponentLoader />;

  return (
    <Grid container item xs={12} direction="column">
      <CustomPaper className="paper paper-tight">
        {/* Placeholder */}
        <Grid container className="space" marginTop={2}>
          <Grid item display="flex" alignItems="center" justifyContent="center">
            <span style={{ fontSize: '14px', width: 'fit-content' }}>Placeholder</span>
          </Grid>
          <Grid item xs>
            <Grid container marginLeft={2}>
              <W86PlaceholderTextField
                value={storeData.key}
                error={errorMessage.length > 0}
                helperText={errorMessage}
                placeholderText="Enter placeholder for time delay"
                handleChange={handleEditPlaceholder}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Delay type */}
        <Grid xs={12} container item direction="column" className="space" alignItems="baseline">
          <p>Delay execution until</p>
          <FormControl component="fieldset">
            <RadioGroup
              name="delay-execution-type"
              value={componentType}
              onChange={(e) => {
                const val = Number(e.target.value);
                setComponentType(val);
                store.getState().setData(val, 'mode');
              }}
            >
              <FormControlLabel value={0} control={<Radio />} label="a certain amount of time has elapsed" />
              <FormControlLabel value={1} control={<Radio />} label="a specific date and time" />
              <FormControlLabel value={2} control={<Radio />} label="a certain day and time of the week" />
              <FormControlLabel value={3} control={<Radio />} label="a datetime placeholder value" />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* TIME_ELAPSED: delay amount + period */}
        {componentType === 0 && (
          <Grid xs={12} container item direction="row" className="row-padding16" alignItems="center" spacing={2}>
            <Grid item xs={12} className="flex-col-gap8">
              <p className="chipinput-description">Enter the amount of time to delay by (number or placeholder):</p>
              <div className="flex-row-center-gap16">
                <ChipInput
                  placeholders={props.availablePlaceholders}
                  placeholder="Insert number or placeholder"
                  inputValue={delayBy}
                  size="small" fullWidth
                  onPlaceholderSelected={props.onPlaceholderSelected}
                  onDelete={(index) => {
                    const next = [...delayBy];
                    next.splice(index, 1);
                    setDelayBy(next);
                    store.getState().setData(next[0] || '', 'delayAmount');
                  }}
                  onFocus={() => setFocus('delayBy')}
                />
                <Select
                  placeholder="select period"
                  value={period}
                  className="select-period"
                  onChange={(e) => {
                    setPeriod(e.target.value);
                    store.getState().setData(e.target.value, 'delayUnit');
                  }}
                >
                  <MenuItem value={0}>Select period</MenuItem>
                  <MenuItem value={1}>minutes</MenuItem>
                  <MenuItem value={2}>hours</MenuItem>
                  <MenuItem value={3}>days</MenuItem>
                  <MenuItem value={6}>business days</MenuItem>
                  <MenuItem value={4}>weeks</MenuItem>
                  <MenuItem value={5}>months</MenuItem>
                </Select>
              </div>
            </Grid>
          </Grid>
        )}

        {/* CERTAIN_DATE_TIME: date picker */}
        {componentType === 1 && (
          <Grid container item xs={12} direction="column">
            <Grid container item xs={12} direction="row" alignItems="center" className="lift row-padding16-0">
              <p className="spaceItems">At this date</p>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={date ? dayjs(date) : null}
                  format="DD.MM.YYYY"
                  onChange={handleSetDateTime}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        )}

        {/* CERTAIN_WEEK_DAY: day buttons */}
        {componentType === 2 && (
          <Grid container item xs={12} direction="column" className="lift row-padding16">
            <Grid container item xs={12} direction="row" alignItems="baseline">
              <p className="spaceItems">Select day</p>
              <ButtonGroup>
                {WEEK_DAYS.map((d) => (
                  <Button
                    key={d}
                    className="d-button"
                    variant={day === d ? 'contained' : 'outlined'}
                    color={day === d ? 'primary' : 'info'}
                    onClick={() => { setDay(d); store.getState().setData(d, 'dayOfWeek'); }}
                  >
                    {d}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
          </Grid>
        )}

        {/* Time picker (for date/time and day-of-week modes) */}
        {(componentType === 1 || componentType === 2) && (
          <Grid container item xs={12} direction="row" alignItems="center" className="space">
            <p className="spaceItems">At this time</p>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker onChange={handleSetDateTime} value={date ? dayjs(date) : null} />
            </LocalizationProvider>
          </Grid>
        )}

        {/* Timezone (for date/time and day-of-week modes) */}
        {(componentType === 1 || componentType === 2) && (
          <Grid container item xs={12} direction="row" alignItems="center" className="space lift">
            <p className="spaceItems">Timezone</p>
            <Autocomplete
              className="autocomplete"
              options={timezones}
              value={timezones.find((tz) => tz.name === timezone) || null}
              size="small"
              onChange={(_, val) => {
                if (!val) return;
                setTimezone(val.name);
                store.getState().setData(val.name, 'timezone');
              }}
              renderInput={(params) => <TextField {...params} variant="outlined" />}
              getOptionLabel={(opt) => (typeof opt === 'object' ? `${opt.name} ${opt.offset}` : opt)}
              isOptionEqualToValue={(opt, val) => opt?.name === val?.name}
            />
          </Grid>
        )}

        {/* AT_DATE_TIME_PLACEHOLDER: placeholder chip input */}
        {componentType === 3 && (
          <Grid xs={12} container item direction="row" className="row-padding16" alignItems="center" spacing={2}>
            <Grid item xs={12} className="flex-col-gap8">
              <p className="chipinput-description">Enter a datetime placeholder to delay until a specific date and time:</p>
              <ChipInput
                placeholders={props.availablePlaceholders}
                placeholder="Insert datetime placeholder"
                inputValue={atDatetimePlaceholder}
                size="small" fullWidth
                onPlaceholderSelected={props.onPlaceholderSelected}
                onDelete={(index) => {
                  const next = [...atDatetimePlaceholder];
                  next.splice(index, 1);
                  setAtDatetimePlaceholder(next);
                  store.getState().setData(next[0] || '', 'datetimePlaceholder');
                }}
                onFocus={() => setFocus('atDatetimePlaceholder')}
              />
            </Grid>
          </Grid>
        )}
      </CustomPaper>
    </Grid>
  );
};

export default TimeDelayTemplate;
